import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Gauge, HeartHandshake, Rocket, Crosshair } from "lucide-react";
import { Chapter, SectionTitle, SectionSub, EASE } from "./shared";

const CountUp = ({ to, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} className="font-display tabular-nums">
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
};

const Bar = ({ pct, gradient, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div
      ref={ref}
      className="relative h-1.5 w-full overflow-hidden rounded-full bg-white/[0.07]"
    >
      <div
        style={{ width: inView ? `${pct}%` : "0%", transitionDelay: `${delay}s` }}
        className={`absolute inset-y-0 left-0 rounded-full transition-[width] duration-[1400ms] ease-out ${gradient}`}
      />
    </div>
  );
};

const MetricRow = ({ name, dot, valueNode, pct, gradient, delay }) => (
  <div>
    <div className="mb-2 flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-slate-400">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        {name}
      </span>
      <span className="text-lg font-bold text-white">{valueNode}</span>
    </div>
    <Bar pct={pct} gradient={gradient} delay={delay} />
  </div>
);

const LOV_GRADIENT = "bg-gradient-to-r from-[#FF5C8A] to-[#8B5CF6]";
const EMG_GRADIENT = "bg-gradient-to-r from-blue-500 to-cyan-400";

const CARDS = [
  {
    icon: Gauge,
    key: "build-speed",
    title: "Build Speed",
    note: "Time to first working app · lower is better",
    rows: [
      { name: "Lovable", to: 2, suffix: " min", pct: 50, gradient: LOV_GRADIENT, dot: "bg-[#FF2D55]" },
      { name: "Emergent", to: 4, suffix: " min", pct: 100, gradient: EMG_GRADIENT, dot: "bg-cyan-400" },
    ],
  },
  {
    icon: HeartHandshake,
    key: "user-satisfaction",
    title: "User Satisfaction",
    note: "Average community rating, 2026",
    rows: [
      { name: "Lovable", to: 4.7, decimals: 1, suffix: "/5", pct: 94, gradient: LOV_GRADIENT, dot: "bg-[#FF2D55]" },
      { name: "Emergent", to: 4.8, decimals: 1, suffix: "/5", pct: 96, gradient: EMG_GRADIENT, dot: "bg-cyan-400" },
    ],
  },
  {
    icon: Rocket,
    key: "deployment-time",
    title: "Deployment Time",
    note: "From done to live URL · lower is better",
    rows: [
      { name: "Lovable", to: 30, suffix: "s", pct: 67, gradient: LOV_GRADIENT, dot: "bg-[#FF2D55]" },
      { name: "Emergent", to: 45, suffix: "s", pct: 100, gradient: EMG_GRADIENT, dot: "bg-cyan-400" },
    ],
  },
  {
    icon: Crosshair,
    key: "ai-accuracy",
    title: "AI Accuracy",
    note: "First-pass code correctness in our task suite",
    rows: [
      { name: "Lovable", to: 89, suffix: "%", pct: 89, gradient: LOV_GRADIENT, dot: "bg-[#FF2D55]" },
      { name: "Emergent", to: 93, suffix: "%", pct: 93, gradient: EMG_GRADIENT, dot: "bg-cyan-400" },
    ],
  },
];

export default function Metrics() {
  return (
    <section id="metrics" className="relative py-11">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Chapter index="03" label="Benchmark Telemetry" testId="metrics-chapter-badge" />
        <SectionTitle>
          Performance, <span className="text-gradient-cyan">measured</span>
        </SectionTitle>
        <SectionSub>
          Numbers from our standardized build tests — same prompts, same deadlines, both platforms.
        </SectionSub>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {CARDS.map((card, i) => (
            <motion.div
              key={card.key}
              data-testid={`metric-card-${card.key}`}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-70px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              whileHover={{ y: -6 }}
              className="group rounded-3xl border border-white/10 bg-[#0A0A0A]/80 p-7 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 hover:border-blue-500/40 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.35)]"
            >
              <div className="mb-6 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-blue-500/30 bg-blue-500/10 shadow-[0_0_20px_rgba(59,130,246,0.25)]">
                  <card.icon className="h-5 w-5 text-cyan-300" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold text-white">{card.title}</h3>
                  <p className="text-xs text-slate-500">{card.note}</p>
                </div>
              </div>
              <div className="space-y-5">
                {card.rows.map((row, j) => (
                  <MetricRow
                    key={row.name}
                    name={row.name}
                    dot={row.dot}
                    pct={row.pct}
                    gradient={row.gradient}
                    delay={0.2 + j * 0.15}
                    valueNode={
                      <CountUp to={row.to} decimals={row.decimals || 0} suffix={row.suffix} />
                    }
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
